/**
 * Firestore `sweepLocations` + `readers` subcollection — parity with skimbuster-mobile.
 *
 * If the web app gets permission-denied, confirm Firebase rules allow the signed-in user
 * to read/write documents where `masterOwnerID` matches their tenant (same as mobile app).
 * There is no rules file in this repo; verify in Firebase Console → Firestore → Rules.
 */
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  limit,
  setDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { SweepCheckedOffReason, SweepFolder, SweepLocation, SweepReader } from '../types/sweep';
import { filterSweepLocationsForReportSiteBrowse } from '../utils/sweepSchedule';

const SWEEP_LOCATIONS = 'sweepLocations';
const SWEEP_FOLDERS = 'sweepFolders';
const DELETE_BATCH_SIZE = 400;

const norm = (v: unknown) => String(v ?? '').trim();

const deleteSweepReadersForLocation = async (sweepLocationId: string): Promise<void> => {
  const docId = norm(sweepLocationId);
  if (!docId) return;

  while (true) {
    const snap = await getDocs(
      query(collection(db, SWEEP_LOCATIONS, docId, 'readers'), limit(DELETE_BATCH_SIZE))
    );
    if (snap.docs.length === 0) return;

    const batch = writeBatch(db);
    for (const readerDoc of snap.docs) {
      batch.delete(readerDoc.ref);
    }
    await batch.commit();

    if (snap.docs.length < DELETE_BATCH_SIZE) return;
  }
};

const fnv1a32 = (input: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

export const SweepsFirestore = {
  makeSweepLocationId(opts: {
    masterOwnerID: string;
    name: string;
    address: string;
    teamNumber?: number | null;
  }): string {
    const mo = norm(opts.masterOwnerID).toLowerCase();
    const nm = norm(opts.name).toLowerCase();
    const ad = norm(opts.address).toLowerCase();
    const hasTeam = typeof opts.teamNumber === 'number' && Number.isFinite(opts.teamNumber);
    const key = hasTeam
      ? `${mo}|${nm}|${ad}|t:${Math.round(opts.teamNumber as number)}`
      : `${mo}|${nm}|${ad}`;
    return `sweep_${fnv1a32(key)}`;
  },

  async listSweepFolders(masterOwnerID: string): Promise<SweepFolder[]> {
    const id = norm(masterOwnerID);
    if (!id) return [];
    const q = query(collection(db, SWEEP_FOLDERS), where('masterOwnerID', '==', id), limit(200));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) })) as SweepFolder[];
    rows.sort((a, b) => {
      const aa = a.archived ? 1 : 0;
      const bb = b.archived ? 1 : 0;
      if (aa !== bb) return aa - bb;
      const da = String(a.sweepDate ?? '').trim();
      const db = String(b.sweepDate ?? '').trim();
      // YYYY-MM-DD: ascending (earliest sweep first). Folders without a date sort after dated ones, then by name.
      if (da && db && da !== db) return da.localeCompare(db);
      if (db && !da) return 1;
      if (da && !db) return -1;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    });
    return rows;
  },

  async createSweepFolder(opts: {
    masterOwnerID: string;
    ownerID?: string;
    name: string;
    /** YYYY-MM-DD — shown in folder list and applied to imported stops when used from import flow. */
    sweepDate?: string | null;
  }): Promise<string> {
    const masterOwnerID = norm(opts.masterOwnerID);
    const name = norm(opts.name);
    if (!masterOwnerID) throw new Error('masterOwnerID is required');
    if (!name) throw new Error('Sweep name is required');
    const ref = doc(collection(db, SWEEP_FOLDERS));
    const now = serverTimestamp();
    const sd = norm(String(opts.sweepDate ?? ''));
    const sortOrder =
      sd && /^\d{4}-\d{2}-\d{2}$/.test(sd) ? -new Date(`${sd}T12:00:00`).getTime() : Date.now();
    await setDoc(ref, {
      masterOwnerID,
      name,
      ...(sd && /^\d{4}-\d{2}-\d{2}$/.test(sd) ? { sweepDate: sd } : {}),
      sortOrder,
      createdAt: now,
      updatedAt: now,
      ...(opts.ownerID ? { createdByOwnerID: norm(opts.ownerID) } : {}),
    });
    return ref.id;
  },

  async updateSweepFolder(
    folderId: string,
    patch: { name?: string; sweepDate?: string | null; archived?: boolean }
  ): Promise<void> {
    const fid = norm(folderId);
    if (!fid) return;
    if (patch.name !== undefined && !norm(patch.name)) throw new Error('Sweep name cannot be empty');
    const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (patch.name !== undefined) payload.name = norm(patch.name);
    if (patch.sweepDate !== undefined) {
      const sd = norm(String(patch.sweepDate ?? ''));
      if (!sd) {
        payload.sweepDate = deleteField();
        payload.sortOrder = Date.now();
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(sd)) {
        payload.sweepDate = sd;
        payload.sortOrder = -new Date(`${sd}T12:00:00`).getTime();
      }
    }
    if (patch.archived !== undefined) {
      payload.archived = patch.archived;
    }
    await setDoc(doc(db, SWEEP_FOLDERS, fid), payload, { merge: true });
  },

  /**
   * Ensures each non-empty folder name has a `sweepFolders` doc. Returns map: lowercase(trim(name)) → folder id.
   */
  async ensureSweepFolderIdsForNames(
    masterOwnerID: string,
    ownerID: string | undefined,
    names: string[]
  ): Promise<Map<string, string>> {
    const mo = norm(masterOwnerID);
    const map = new Map<string, string>();
    if (!mo) return map;
    const existing = await this.listSweepFolders(mo);
    for (const f of existing) {
      if (f.archived) continue;
      const k = norm(f.name).toLowerCase();
      if (k) map.set(k, f.id);
    }
    const uniq = Array.from(
      new Set(names.map((n) => norm(n).toLowerCase()).filter((k) => k.length > 0))
    );
    for (const k of uniq) {
      if (map.has(k)) continue;
      const display = names.find((n) => norm(n).toLowerCase() === k) || k;
      const id = await this.createSweepFolder({ masterOwnerID: mo, ownerID, name: display });
      map.set(k, id);
    }
    return map;
  },

  async deleteSweepFolder(folderId: string, masterOwnerID: string): Promise<void> {
    const fid = norm(folderId);
    const mo = norm(masterOwnerID);
    if (!fid || !mo) return;
    const locs = await this.listSweepLocations(mo);
    const toClear = locs.filter((l) => norm(l.sweepFolderId) === fid).map((l) => String(l.id));
    await this.bulkSetSweepFolderId(toClear, null);
    await deleteDoc(doc(db, SWEEP_FOLDERS, fid));
  },

  async bulkSetSweepFolderId(ids: string[], sweepFolderId: string | null): Promise<void> {
    const uniq = Array.from(new Set(ids.map((x) => norm(x)))).filter(Boolean);
    if (!uniq.length) return;
    const CHUNK = 400;
    for (let i = 0; i < uniq.length; i += CHUNK) {
      const slice = uniq.slice(i, i + CHUNK);
      const batch = writeBatch(db);
      for (const docId of slice) {
        const ref = doc(db, SWEEP_LOCATIONS, docId);
        if (sweepFolderId == null || sweepFolderId === '') {
          batch.set(ref, { sweepFolderId: deleteField(), updatedAt: serverTimestamp() }, { merge: true });
        } else {
          batch.set(
            ref,
            { sweepFolderId: norm(sweepFolderId), updatedAt: serverTimestamp() },
            { merge: true }
          );
        }
      }
      await batch.commit();
    }
  },

  async listSweepLocations(masterOwnerID: string): Promise<SweepLocation[]> {
    const id = norm(masterOwnerID);
    if (!id) return [];
    const q = query(
      collection(db, SWEEP_LOCATIONS),
      where('masterOwnerID', '==', id)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) })) as SweepLocation[];
  },

  /** Same as list + client filter for Report Site browse (not checked off; scheduled date unset or equals today). */
  async listSweepLocationsForReportSiteBrowse(masterOwnerID: string, todayYmd: string): Promise<SweepLocation[]> {
    const all = await this.listSweepLocations(masterOwnerID);
    return filterSweepLocationsForReportSiteBrowse(all, todayYmd);
  },

  async bulkSetSweepScheduledDate(ids: string[], sweepScheduledDate: string | null): Promise<void> {
    const uniq = Array.from(new Set(ids.map((x) => norm(x)))).filter(Boolean);
    if (!uniq.length) return;
    const CHUNK = 400;
    for (let i = 0; i < uniq.length; i += CHUNK) {
      const slice = uniq.slice(i, i + CHUNK);
      const batch = writeBatch(db);
      for (const docId of slice) {
        const ref = doc(db, SWEEP_LOCATIONS, docId);
        if (sweepScheduledDate == null || sweepScheduledDate === '') {
          batch.set(
            ref,
            { sweepScheduledDate: deleteField(), updatedAt: serverTimestamp() },
            { merge: true }
          );
        } else {
          batch.set(
            ref,
            { sweepScheduledDate: norm(sweepScheduledDate), updatedAt: serverTimestamp() },
            { merge: true }
          );
        }
      }
      await batch.commit();
    }
  },

  async upsertSweepLocation(
    opts: {
      masterOwnerID: string;
      ownerID?: string;
      importBatchId?: string;
      id?: string;
    },
    patch: Omit<Partial<SweepLocation>, 'id' | 'masterOwnerID'> & Record<string, unknown>
  ): Promise<string> {
    const masterOwnerID = norm(opts.masterOwnerID);
    if (!masterOwnerID) throw new Error('masterOwnerID is required');

    const name = norm((patch as Partial<SweepLocation>)?.name);
    const address = norm((patch as Partial<SweepLocation>)?.address);

    const teamNum = (patch as Partial<SweepLocation>)?.teamNumber;
    const docId =
      norm(opts.id) ||
      (name && address
        ? this.makeSweepLocationId({
            masterOwnerID,
            name,
            address,
            teamNumber:
              typeof teamNum === 'number' && Number.isFinite(teamNum) ? Math.round(teamNum) : null,
          })
        : doc(collection(db, SWEEP_LOCATIONS)).id);

    const ref = doc(db, SWEEP_LOCATIONS, docId);
    const now = serverTimestamp();

    const data: Record<string, unknown> = {
      masterOwnerID,
      ...patch,
      updatedAt: now,
    };
    if (!norm(opts.id) && opts.ownerID) data.createdByOwnerID = norm(opts.ownerID);
    if (opts.importBatchId) data.importBatchId = norm(opts.importBatchId);
    if (!norm(opts.id)) data.createdAt = now;

    await setDoc(ref, data, { merge: true });
    return docId;
  },

  /**
   * Renumbers `stopSequence` to 1..n within each (sweepFolderId, teamNumber) group for the tenant.
   * Call after bulk team changes so stop numbers stay contiguous per folder+team.
   */
  async bulkRenumberAllSweepStopSequences(masterOwnerID: string): Promise<void> {
    const mo = norm(masterOwnerID);
    if (!mo) return;
    const all = await this.listSweepLocations(mo);
    const normFolder = (loc: SweepLocation) => norm(String(loc.sweepFolderId ?? ''));
    const teamRound = (loc: SweepLocation): number | null => {
      const t = loc.teamNumber;
      return typeof t === 'number' && Number.isFinite(t) ? Math.round(t) : null;
    };
    const byGroup = new Map<string, SweepLocation[]>();
    for (const loc of all) {
      const k = `${normFolder(loc)}|${teamRound(loc) ?? '_'}`;
      if (!byGroup.has(k)) byGroup.set(k, []);
      byGroup.get(k)!.push(loc);
    }
    const stableSort = (a: SweepLocation, b: SweepLocation): number => {
      const sa = typeof a.stopSequence === 'number' && Number.isFinite(a.stopSequence) ? Math.round(a.stopSequence) : null;
      const sb = typeof b.stopSequence === 'number' && Number.isFinite(b.stopSequence) ? Math.round(b.stopSequence) : null;
      if (sa == null && sb == null) {
        const nm = String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
        if (nm !== 0) return nm;
        return String(a.id).localeCompare(String(b.id));
      }
      if (sa == null) return 1;
      if (sb == null) return -1;
      if (sa !== sb) return sa - sb;
      return String(a.id).localeCompare(String(b.id));
    };
    const updates: { id: string; seq: number }[] = [];
    for (const [, arr] of Array.from(byGroup.entries())) {
      if (arr.length === 0) continue;
      const sorted = [...arr].sort(stableSort);
      let seq = 1;
      for (const loc of sorted) {
        const cur =
          typeof loc.stopSequence === 'number' && Number.isFinite(loc.stopSequence)
            ? Math.round(loc.stopSequence)
            : null;
        if (cur !== seq) updates.push({ id: String(loc.id), seq });
        seq += 1;
      }
    }
    const CHUNK = 400;
    for (let i = 0; i < updates.length; i += CHUNK) {
      const slice = updates.slice(i, i + CHUNK);
      const batch = writeBatch(db);
      for (const u of slice) {
        const docId = norm(u.id);
        if (!docId) continue;
        batch.set(
          doc(db, SWEEP_LOCATIONS, docId),
          { stopSequence: u.seq, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }
      await batch.commit();
    }
  },

  async deleteSweepLocation(id: string): Promise<void> {
    const docId = norm(id);
    if (!docId) return;
    await deleteSweepReadersForLocation(docId);
    await deleteDoc(doc(db, SWEEP_LOCATIONS, docId));
  },

  /**
   * Check off (hide from browse) or restore. When restoring, reason/notes are cleared.
   * When checking off without `meta`, only `sweepCheckedOff` is set (legacy); prefer passing `meta.reason`.
   */
  async setSweepLocationCheckedOff(
    id: string,
    checkedOff: boolean,
    meta?: { reason?: SweepCheckedOffReason; notes?: string }
  ): Promise<void> {
    const docId = norm(id);
    if (!docId) return;
    const ref = doc(db, SWEEP_LOCATIONS, docId);
    if (!checkedOff) {
      await setDoc(
        ref,
        {
          sweepCheckedOff: false,
          sweepCheckedOffReason: deleteField(),
          sweepCheckedOffNotes: deleteField(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      return;
    }
    const payload: Record<string, unknown> = {
      sweepCheckedOff: true,
      updatedAt: serverTimestamp(),
    };
    if (meta?.reason) payload.sweepCheckedOffReason = meta.reason;
    if (meta?.notes !== undefined) payload.sweepCheckedOffNotes = norm(meta.notes);
    await setDoc(ref, payload, { merge: true });
  },

  async bulkDeleteSweepLocations(ids: string[]): Promise<void> {
    const uniq = Array.from(new Set(ids.map((x) => norm(x)))).filter(Boolean);
    if (!uniq.length) return;
    for (const docId of uniq) {
      await deleteSweepReadersForLocation(docId);
    }
    const CHUNK = 400;
    for (let i = 0; i < uniq.length; i += CHUNK) {
      const slice = uniq.slice(i, i + CHUNK);
      const batch = writeBatch(db);
      for (const docId of slice) {
        batch.delete(doc(db, SWEEP_LOCATIONS, docId));
      }
      await batch.commit();
    }
  },

  async listSweepReaders(sweepLocationId: string): Promise<SweepReader[]> {
    const docId = norm(sweepLocationId);
    const snap = await getDocs(query(collection(db, SWEEP_LOCATIONS, docId, 'readers'), limit(500)));
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) })) as SweepReader[];
  },

  async addSweepReader(sweepLocationId: string, reader: { label: string; notes?: string }): Promise<string> {
    const docId = norm(sweepLocationId);
    const ref = doc(collection(db, SWEEP_LOCATIONS, docId, 'readers'));
    await setDoc(ref, {
      label: norm(reader.label),
      notes: norm(reader.notes) || '',
      createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  async deleteSweepReader(sweepLocationId: string, readerId: string): Promise<void> {
    const sid = norm(sweepLocationId);
    const rid = norm(readerId);
    await deleteDoc(doc(db, SWEEP_LOCATIONS, sid, 'readers', rid));
  },

  async updateReaderCountFromReaders(sweepLocationId: string): Promise<void> {
    const readers = await this.listSweepReaders(sweepLocationId);
    await setDoc(
      doc(db, SWEEP_LOCATIONS, norm(sweepLocationId)),
      {
        readerCount: readers.length,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  },
} as const;
